import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { 
  ChevronDown, ChevronUp, LogOut, Download, Search, 
  Filter, Users, TrendingUp, BarChart3, FileText, 
  Eye, RefreshCw, AlertCircle, Trash2, X, AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Question definitions for analytics display (same as main app)
const QUESTIONS = {
  buying_priorities: {
    title: "What matters to you most when buying beauty products?",
    options: [
      "Dermatologically safe",
      "Visible results",
      "Clean ingredients",
      "Premium packaging",
      "Value for money",
      "Brand and ingredients transparency",
      "Natural formulations"
    ]
  },
  skin_concerns: {
    title: "Which of the following best describes your current concerns?",
    options: [
      "Skin gets tanned easily",
      "Early fine lines, wrinkles or sagging",
      "Pigmentation or uneven skin tone",
      "Sensitive or easily irritated skin",
      "Dull, tired-looking complexion",
      "Acne, breakouts or excess oil",
      "Dehydrated, dry or tight-feeling skin",
      "Dry Pigmented Lips"
    ]
  },
  skin_goals: {
    title: "Your main skincare goal is to:",
    options: [
      "Look firmer and more youthful",
      "Correct pigmentation precisely",
      "Calm and soothe skin",
      "Restore overall balance",
      "Achieve visible glow",
      "Feel fresh and energized",
      "Deeply hydrate and nourish",
      "Strengthen and protect skin"
    ]
  },
  hair_concerns: {
    title: "What is your biggest hair concern?",
    options: [
      "Hair thinning or lack of volume",
      "Dry, frizzy or rough texture",
      "Sensitive or itchy scalp",
      "Oily scalp",
      "Dull, lifeless hair",
      "Weak roots or breakage",
      "Just want smooth, healthy-looking hair"
    ]
  },
  hair_feel_goals: {
    title: "How do you want your hair to feel?",
    options: [
      "Silky and luxurious",
      "Strong and resilient",
      "Light and fresh",
      "Soft and hydrated",
      "Balanced and healthy"
    ]
  },
  routine_feeling: {
    title: "Which feeling do you want your beauty routine to give you?",
    options: [
      "Calm and balanced",
      "Radiant and glowing",
      "Fresh and energized",
      "Deeply nourished",
      "Youthful and lifted",
      "Clear and refined",
      "Don't have any Beauty Routine"
    ]
  },
  texture_preference: {
    title: "What texture do you enjoy the most in skincare?",
    options: [
      "Rich and indulgent cream",
      "Lightweight gel",
      "Fast-absorbing serum",
      "Soft lotion",
      "Oil-based nourishment",
      "Minimal, barely-there formula"
    ]
  },
  fragrance_preference: {
    title: "Your fragrance preference:",
    options: [
      "Subtle premium scent",
      "Fresh botanical notes",
      "Completely fragrance-free",
      "Soft floral",
      "Citrus and uplifting",
      "Warm and comforting"
    ]
  },
  makeup_vibe: {
    title: "Your makeup vibe is:",
    options: [
      "Minimal & pure",
      "Bold & powerful",
      "Glowy & luminous",
      "Flawless & even",
      "Elegant & timeless"
    ]
  },
  makeup_coverage: {
    title: "Makeup Coverage preference:",
    options: [
      "Natural everyday finish",
      "Medium coverage",
      "Full coverage",
      "Only targeted correction",
      "I hardly wear any make-up"
    ]
  },
  brand_disappointments: {
    title: "What disappoints you most in beauty brands?",
    options: [
      "Overpromising but not delivering real results",
      "Lack of transparency in ingredients",
      "Too many harsh chemicals",
      "Products not suited for Indian skin/climate",
      "No real solution for pigmentation",
      "Poor customer support or brand interaction",
      "Overpriced without visible difference",
      "Influencer-driven hype with no substance",
      "Complicated routines with too many products"
    ]
  },
  brand_solutions: {
    title: "If one brand could solve 3 major issues for you, what would that be?",
    options: [
      "Persistent pigmentation / uneven skin tone",
      "Under eye dark circle",
      "Dull, tired-looking skin",
      "Hormonal acne / recurring breakouts",
      "Dark underarms / elbows / knees",
      "Hair fall / thinning hair",
      "Frizz / dry damaged hair",
      "Sensitive or reactive skin",
      "Long-lasting natural makeup for Indian skin tone",
      "Dark pigmented lips"
    ]
  },
  body_skin: {
    title: "How would you describe your body skin?",
    options: [
      "Very dry",
      "Rough or uneven",
      "Healthy but needs nourishment",
      "Smooth and resilient",
      "Dark elbows, Knees, Neck and Underarms",
      "Cracked Heels/Rough Feet",
      "Rough Hands"
    ]
  },
  early_access: {
    title: "Would you like early access to launches, special pricing & private previews?",
    options: [
      "Yes, absolutely — I love early access",
      "Maybe, if benefits are clear",
      "Not sure yet",
      "No"
    ]
  }
};

const questionOrder = [
  "buying_priorities",
  "skin_concerns",
  "skin_goals",
  "hair_concerns",
  "hair_feel_goals",
  "routine_feeling",
  "texture_preference",
  "fragrance_preference",
  "makeup_vibe",
  "makeup_coverage",
  "brand_disappointments",
  "brand_solutions",
  "body_skin",
  "early_access"
];

// Utility functions
function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row).forEach((k) => set.add(k));
      return set;
    }, new Set())
  );
  const csv = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const value = row[header] ?? "";
          const str = typeof value === "object" ? JSON.stringify(value) : String(value);
          return `"${str.replace(/"/g, '""')}"`;
        })
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function extractFlattenedRow(submission) {
  const q = submission.questionnaire_data || {};
  const u = submission.user_info || {};
  const a = submission.aura_result || {};
  return {
    id: submission.id || "",
    created_at: submission.created_at || "",
    name: u.name || "",
    phone: u.phone || "",
    email: u.email || "",
    address: u.address || "",
    preferred_contact_method: u.preferred_contact_method || "",
    analysis_mode: submission.analysis_mode || "",
    ask_dermat: submission.ask_dermat ? "Yes" : "No",
    primary_aura: a.primary_aura || "",
    secondary_aura: a.secondary_aura || "",
    energy_state: a.energy_state || "",
    detected_flags: (submission.detected_flags || []).join(", "),
    notes: submission.notes || "",
    questionnaire_data: JSON.stringify(q),
  };
}

function extractDateKey(value) {
  if (!value) return "Unknown";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "Unknown";
  return d.toISOString().slice(0, 10);
}

function barWidth(value, max) {
  if (!max) return "0%";
  return `${(value / max) * 100}%`;
}

function safeArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}

function countItems(items) {
  const map = {};
  items.forEach((item) => {
    map[item] = (map[item] || 0) + 1;
  });
  return map;
}

function topN(map, n = 5) {
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}

// Login Component
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.success) {
        localStorage.setItem("adminToken", res.data.token);
        onLogin(res.data.token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Divine Aura</h1>
            <p className="text-purple-200">Admin Panel</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="Enter username"
                required
                data-testid="admin-username-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="Enter password"
                required
                data-testid="admin-password-input"
              />
            </div>
            
            {error && (
              <div className="flex items-center gap-2 text-red-300 text-sm bg-red-500/20 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="admin-login-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Question Analytics Card Component
function QuestionAnalyticsCard({ questionKey, questionData, submissions }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Calculate response counts for this question
  const analytics = useMemo(() => {
    const responseCounts = {};
    let totalResponses = 0;
    
    submissions.forEach((submission) => {
      const qData = submission.questionnaire_data || {};
      const answers = safeArray(qData[questionKey]);
      
      answers.forEach((answer) => {
        responseCounts[answer] = (responseCounts[answer] || 0) + 1;
        totalResponses++;
      });
    });
    
    // Convert to array and sort by count (descending)
    const sortedOptions = Object.entries(responseCounts)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
    
    const maxCount = sortedOptions.length > 0 ? sortedOptions[0][1] : 0;
    
    return {
      sortedOptions,
      totalResponses,
      maxCount,
      uniqueRespondents: submissions.filter(s => {
        const answers = safeArray((s.questionnaire_data || {})[questionKey]);
        return answers.length > 0;
      }).length
    };
  }, [questionKey, submissions]);
  
  if (analytics.sortedOptions.length === 0) {
    return null; // Don't render if no responses
  }
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-testid={`question-card-${questionKey}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex-1 text-left">
          <h3 className="font-semibold text-slate-800 text-sm leading-tight">
            {questionData.title}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {analytics.uniqueRespondents} responses • {analytics.sortedOptions.length} options selected
          </p>
        </div>
        <div className="ml-4 flex items-center gap-2">
          <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
            {analytics.totalResponses} selections
          </span>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>
      
      {isExpanded && (
        <div className="px-5 pb-5 space-y-3 border-t border-slate-100 pt-4">
          {analytics.sortedOptions.map(([option, count]) => {
            const percentage = analytics.totalResponses > 0 
              ? Math.round((count / analytics.totalResponses) * 100) 
              : 0;
            
            return (
              <div key={option} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 flex-1 pr-4 truncate" title={option}>
                    {option}
                  </span>
                  <span className="text-slate-500 whitespace-nowrap">
                    {count} ({percentage}%)
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
                    style={{ width: barWidth(count, analytics.maxCount) }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Main Admin Dashboard Component
function AdminDashboard({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [auraFilter, setAuraFilter] = useState("all");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Delete functionality states
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'single' | 'bulk', submission?: object, ids?: string[] }
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { "X-Admin-Token": token }
      });
      setSubmissions(res.data.submissions || []);
      setError("");
      // Clear selections after refresh
      setSelectedIds(new Set());
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        onLogout();
      } else {
        setError("Failed to fetch submissions");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchSubmissions();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSubmissions, 30000);
    return () => clearInterval(interval);
  }, [fetchSubmissions]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSubmissions();
  };

  // Toggle selection for a single submission
  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Toggle select all (filtered submissions only)
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredSubmissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredSubmissions.map(s => s.id)));
    }
  };

  // Open delete confirmation modal for single record
  const openDeleteSingle = (submission, e) => {
    e.stopPropagation();
    setDeleteTarget({ type: 'single', submission });
    setDeleteConfirmText("");
    setDeleteModalOpen(true);
  };

  // Open delete confirmation modal for bulk delete
  const openDeleteBulk = () => {
    const ids = Array.from(selectedIds);
    setDeleteTarget({ type: 'bulk', ids });
    setDeleteConfirmText("");
    setDeleteModalOpen(true);
  };

  // Execute deletion
  const executeDelete = async () => {
    if (!deleteTarget) return;
    
    // Validate confirmation for bulk delete
    if (deleteTarget.type === 'bulk') {
      const expectedCount = deleteTarget.ids.length.toString();
      if (deleteConfirmText !== expectedCount) {
        setError(`Please type "${expectedCount}" to confirm deletion`);
        return;
      }
    }

    setDeleting(true);
    setError("");

    try {
      if (deleteTarget.type === 'single') {
        await axios.delete(`${API}/admin/submissions/${deleteTarget.submission.id}`, {
          headers: { "X-Admin-Token": token }
        });
      } else {
        await axios.post(`${API}/admin/submissions/bulk-delete`, 
          { submission_ids: deleteTarget.ids },
          { headers: { "X-Admin-Token": token } }
        );
      }
      
      // Close modal and refresh
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      setSelectedSubmission(null);
      await fetchSubmissions();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete submission(s)");
    } finally {
      setDeleting(false);
    }
  };

  // Filter submissions
  const filteredSubmissions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return submissions.filter((s) => {
      const u = s.user_info || {};
      const a = s.aura_result || {};
      const matchesSearch =
        !query ||
        [u.name, u.phone, u.email, u.address, u.preferred_contact_method, s.analysis_mode, a.primary_aura, a.secondary_aura, a.energy_state, s.notes]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      const matchesMode = modeFilter === "all" || (s.analysis_mode || "").toLowerCase() === modeFilter.toLowerCase();
      const matchesAura = auraFilter === "all" || (s.aura_result?.primary_aura || "").toLowerCase() === auraFilter.toLowerCase();
      return matchesSearch && matchesMode && matchesAura;
    });
  }, [submissions, search, modeFilter, auraFilter]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const allConcerns = [];
    const skinConcerns = [];
    const hairConcerns = [];
    const modeCounts = {};
    const dateCounts = {};
    let askDermatCount = 0;

    filteredSubmissions.forEach((s) => {
      const q = s.questionnaire_data || {};
      const skinFields = ["skin_concerns", "current_skin_concerns", "skinConcern", "skin_concern"];
      const hairFields = ["hair_concerns", "hair_scalp_concerns", "scalp_concerns", "hairConcern", "hair_concern"];
      
      skinFields.forEach((field) => {
        const values = safeArray(q[field]);
        skinConcerns.push(...values);
        allConcerns.push(...values);
      });
      
      hairFields.forEach((field) => {
        const values = safeArray(q[field]);
        hairConcerns.push(...values);
        allConcerns.push(...values);
      });

      const mode = s.analysis_mode || "unknown";
      modeCounts[mode] = (modeCounts[mode] || 0) + 1;

      const dateKey = extractDateKey(s.created_at);
      dateCounts[dateKey] = (dateCounts[dateKey] || 0) + 1;

      if (s.ask_dermat) askDermatCount += 1;
    });

    return {
      total: filteredSubmissions.length,
      askDermatCount,
      topConcerns: topN(countItems(allConcerns), 5),
      topSkinConcerns: topN(countItems(skinConcerns), 5),
      topHairConcerns: topN(countItems(hairConcerns), 5),
      modeCounts,
      dateCounts: Object.entries(dateCounts).sort((a, b) => a[0].localeCompare(b[0])),
    };
  }, [filteredSubmissions]);

  const exportRows = filteredSubmissions.map(extractFlattenedRow);

  const maxConcern = Math.max(1, ...summary.topConcerns.map(([, count]) => count), ...summary.topSkinConcerns.map(([, count]) => count), ...summary.topHairConcerns.map(([, count]) => count));
  const maxMode = Math.max(1, ...Object.values(summary.modeCounts));
  const maxDate = Math.max(1, ...summary.dateCounts.map(([, count]) => count));

  const auraOptions = Array.from(new Set(submissions.map((s) => s.aura_result?.primary_aura).filter(Boolean).map((v) => String(v))));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Divine Aura Admin</h1>
            <p className="text-sm text-slate-500">Analytics & Submission Management</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {[
                { id: "dashboard", label: "Dashboard", icon: BarChart3 },
                { id: "view-details", label: "View Details", icon: Eye },
                { id: "question-analytics", label: "Question Analytics", icon: FileText },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-2 md:flex-row">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full md:w-64 pl-10 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="search-input"
                />
              </div>
              <select
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                data-testid="mode-filter"
              >
                <option value="all">All Modes</option>
                <option value="questionnaire">Questionnaire</option>
                <option value="digital">Digital</option>
                <option value="hybrid">Hybrid</option>
              </select>
              <select
                value={auraFilter}
                onChange={(e) => setAuraFilter(e.target.value)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                data-testid="aura-filter"
              >
                <option value="all">All Auras</option>
                {auraOptions.map((aura) => (
                  <option key={aura} value={aura}>{aura}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            {/* Export Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => downloadCSV("divine-aura-submissions.csv", exportRows)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800"
                data-testid="export-csv-button"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => downloadCSV("divine-aura-summary.csv", [{
                  total_submissions: summary.total,
                  ask_dermat_count: summary.askDermatCount,
                  mode_counts: JSON.stringify(summary.modeCounts),
                  top_5_concerns: JSON.stringify(summary.topConcerns),
                  top_5_skin_concerns: JSON.stringify(summary.topSkinConcerns),
                  top_5_hair_concerns: JSON.stringify(summary.topHairConcerns),
                }])}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-900 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                data-testid="export-summary-button"
              >
                <Download className="w-4 h-4" />
                Export Summary
              </button>
            </div>

            {/* Key Metrics */}
            <div className="grid gap-4 md:grid-cols-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Total Submissions</div>
                    <div className="text-2xl font-bold text-slate-900">{summary.total}</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Ask Dermat Count</div>
                    <div className="text-2xl font-bold text-slate-900">{summary.askDermatCount}</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
                <div className="text-sm text-slate-500">Most Used Mode</div>
                <div className="text-xl font-bold text-slate-900 mt-1">
                  {Object.entries(summary.modeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "-"}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
                <div className="text-sm text-slate-500">Top Concern</div>
                <div className="text-xl font-bold text-slate-900 mt-1 truncate" title={summary.topConcerns[0]?.[0]}>
                  {summary.topConcerns[0]?.[0] || "-"}
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Top 5 Concerns */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Top 5 Concerns</h2>
                <div className="space-y-4">
                  {summary.topConcerns.length ? (
                    summary.topConcerns.map(([label, count]) => (
                      <div key={label}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-700 truncate pr-2" title={label}>{label}</span>
                          <span className="text-slate-500">{count}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full">
                          <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{ width: barWidth(count, maxConcern) }} />
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No concern data found.</p>
                  )}
                </div>
              </div>

              {/* Mode Split */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Mode Split</h2>
                <div className="space-y-4">
                  {Object.entries(summary.modeCounts).map(([label, count]) => (
                    <div key={label}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-700">{label}</span>
                        <span className="text-slate-500">{count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full">
                        <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{ width: barWidth(count, maxMode) }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top 5 Skin Concerns */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Top 5 Skin Concerns</h2>
                <div className="space-y-4">
                  {summary.topSkinConcerns.length ? (
                    summary.topSkinConcerns.map(([label, count]) => (
                      <div key={label}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-700 truncate pr-2" title={label}>{label}</span>
                          <span className="text-slate-500">{count}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full">
                          <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{ width: barWidth(count, maxConcern) }} />
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No skin concern data found.</p>
                  )}
                </div>
              </div>

              {/* Top 5 Hair Concerns */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Top 5 Hair / Scalp Concerns</h2>
                <div className="space-y-4">
                  {summary.topHairConcerns.length ? (
                    summary.topHairConcerns.map(([label, count]) => (
                      <div key={label}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-700 truncate pr-2" title={label}>{label}</span>
                          <span className="text-slate-500">{count}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full">
                          <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{ width: barWidth(count, maxConcern) }} />
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No hair/scalp concern data found.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submission Trend */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Submission Trend by Date</h2>
              <div className="space-y-4">
                {summary.dateCounts.length ? (
                  summary.dateCounts.slice(-14).map(([date, count]) => (
                    <div key={date}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-700">{date}</span>
                        <span className="text-slate-500">{count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full">
                        <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{ width: barWidth(count, maxDate) }} />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No trend data found.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* View Details Tab */}
        {activeTab === "view-details" && (
          <div className="space-y-4">
            {/* Selection Actions Bar */}
            {selectedIds.size > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
                <span className="text-sm font-medium text-red-800">
                  {selectedIds.size} record{selectedIds.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={openDeleteBulk}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                  data-testid="bulk-delete-button"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected ({selectedIds.size})
                </button>
              </div>
            )}

            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              {/* Submissions Table */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-200 p-4">
                  <h2 className="text-lg font-semibold text-slate-900">Raw Submission Records</h2>
                  <p className="text-sm text-slate-500 mt-1">Select records to delete or click to view details</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={filteredSubmissions.length > 0 && selectedIds.size === filteredSubmissions.length}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="px-3 py-3 text-left font-semibold text-slate-700">Date</th>
                        <th className="px-3 py-3 text-left font-semibold text-slate-700">Name</th>
                        <th className="px-3 py-3 text-left font-semibold text-slate-700">Phone</th>
                        <th className="px-3 py-3 text-left font-semibold text-slate-700">Mode</th>
                        <th className="px-3 py-3 text-left font-semibold text-slate-700">Aura</th>
                        <th className="px-3 py-3 text-left font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubmissions.map((submission, idx) => (
                        <tr 
                          key={submission.id || idx} 
                          className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selectedSubmission?.id === submission.id ? 'bg-purple-50' : ''} ${selectedIds.has(submission.id) ? 'bg-red-50' : ''}`}
                          onClick={() => setSelectedSubmission(submission)}
                        >
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(submission.id)}
                              onChange={() => toggleSelection(submission.id)}
                              className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                              data-testid={`select-checkbox-${submission.id}`}
                            />
                          </td>
                          <td className="px-3 py-3 text-slate-600 text-xs">{formatDate(submission.created_at)}</td>
                          <td className="px-3 py-3 text-slate-900">{submission.user_info?.name || "-"}</td>
                          <td className="px-3 py-3 text-slate-600">{submission.user_info?.phone || "-"}</td>
                          <td className="px-3 py-3 text-slate-600">{submission.analysis_mode || "-"}</td>
                          <td className="px-3 py-3 text-slate-600">{submission.aura_result?.primary_aura || "-"}</td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSubmission(submission);
                                }}
                                className="px-2 py-1 text-xs font-medium text-slate-700 bg-slate-100 rounded hover:bg-slate-200"
                                data-testid={`view-button-${submission.id}`}
                              >
                                View
                              </button>
                              <button
                                onClick={(e) => openDeleteSingle(submission, e)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete this record"
                                data-testid={`delete-button-${submission.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!filteredSubmissions.length && (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                            No submissions found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Submission Detail */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Submission Detail</h2>
                {selectedSubmission ? (
                  <div className="mt-4 space-y-4 text-sm">
                    <div>
                      <div className="font-semibold text-slate-800">User Info</div>
                      <pre className="mt-2 overflow-auto rounded-xl bg-slate-50 p-3 text-xs text-slate-700 max-h-40">
                        {JSON.stringify(selectedSubmission.user_info || {}, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">Aura Result</div>
                      <pre className="mt-2 overflow-auto rounded-xl bg-slate-50 p-3 text-xs text-slate-700 max-h-40">
                        {JSON.stringify(selectedSubmission.aura_result || {}, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">Questionnaire Data</div>
                      <pre className="mt-2 overflow-auto rounded-xl bg-slate-50 p-3 text-xs text-slate-700 max-h-64">
                        {JSON.stringify(selectedSubmission.questionnaire_data || {}, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">Other Details</div>
                      <pre className="mt-2 overflow-auto rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
                        {JSON.stringify({
                          created_at: selectedSubmission.created_at,
                          analysis_mode: selectedSubmission.analysis_mode,
                          ask_dermat: selectedSubmission.ask_dermat,
                          detected_flags: selectedSubmission.detected_flags,
                          notes: selectedSubmission.notes,
                        }, null, 2)}
                      </pre>
                    </div>
                    {/* Delete button in detail view */}
                    <button
                      onClick={(e) => openDeleteSingle(selectedSubmission, e)}
                      className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                      data-testid="detail-delete-button"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete This Record
                    </button>
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-slate-500">
                    Select a submission to view full raw details.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Question Analytics Tab */}
        {activeTab === "question-analytics" && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Question-wise Response Summary</h2>
              <p className="text-sm text-slate-500 mt-1">
                Click on any question to expand and see detailed response breakdown. Only options with at least 1 response are shown.
              </p>
            </div>
            
            <div className="space-y-3" data-testid="question-analytics-section">
              {questionOrder.map((questionKey, index) => {
                const questionData = QUESTIONS[questionKey];
                if (!questionData) return null;
                return (
                  <QuestionAnalyticsCard
                    key={questionKey}
                    questionKey={questionKey}
                    questionData={questionData}
                    submissions={filteredSubmissions}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Confirm Deletion</h3>
                <p className="text-sm text-slate-500">This action cannot be undone</p>
              </div>
            </div>

            {deleteTarget.type === 'single' ? (
              <div className="mb-6">
                <p className="text-sm text-slate-700 mb-3">
                  You are about to delete the following record:
                </p>
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p><span className="font-medium">Name:</span> {deleteTarget.submission?.user_info?.name || 'N/A'}</p>
                  <p><span className="font-medium">Phone:</span> {deleteTarget.submission?.user_info?.phone || 'N/A'}</p>
                  <p><span className="font-medium">Date:</span> {formatDate(deleteTarget.submission?.created_at)}</p>
                  <p><span className="font-medium">Mode:</span> {deleteTarget.submission?.analysis_mode || 'N/A'}</p>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <p className="text-sm text-slate-700 mb-3">
                  You are about to delete <span className="font-bold text-red-600">{deleteTarget.ids.length} record{deleteTarget.ids.length !== 1 ? 's' : ''}</span>.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-800 font-medium">
                    To confirm, type the number of records to delete:
                  </p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{deleteTarget.ids.length}</p>
                </div>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={`Type "${deleteTarget.ids.length}" to confirm`}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
                  data-testid="delete-confirm-input"
                />
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeleteTarget(null);
                  setDeleteConfirmText("");
                  setError("");
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                data-testid="cancel-delete-button"
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                disabled={deleting || (deleteTarget.type === 'bulk' && deleteConfirmText !== deleteTarget.ids.length.toString())}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-delete-button"
              >
                {deleting ? "Deleting..." : deleteTarget.type === 'single' ? "Delete Record" : `Delete ${deleteTarget.ids.length} Records`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main AdminPanel Component with Auth
export default function AdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem("adminToken"));

  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setToken(null);
  };

  if (!token) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
